import { useMemo, useState, useEffect } from "react";
import { FolderKanban } from "lucide-react";
import { cn } from "@/lib/utils";
import { listProjects, type EngineeringProject } from "@/lib/prometheusProjectsStore";

export type ProjectsSidebarListProps = {
  selectedId?: string | null;
  onSelect: (projectId: string) => void;
  className?: string;
};

export function ProjectsSidebarList({
  selectedId,
  onSelect,
  className,
}: ProjectsSidebarListProps) {
  const [projects, setProjects] = useState<EngineeringProject[]>([]);

  useEffect(() => {
    setProjects(listProjects());
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === "prometheus_engineering_projects" ||
        e.key === "prometheus_lifecycle_projects"
      ) {
        setProjects(listProjects());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const sorted = useMemo(
    () =>
      [...projects].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", "es", { sensitivity: "base" })
      ),
    [projects]
  );

  if (sorted.length === 0) {
    return (
      <div className={cn("px-2.5 py-3 border-t border-border/60 mt-2", className)}>
        <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">
          Proyectos
        </p>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          No hay proyectos en localStorage. Créalos en el Taller legacy.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-0.5 px-1 pt-2 mt-2 border-t border-border/60", className)}>
      <p className="px-2 text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1">
        Observar ({sorted.length})
      </p>
      <ul className="flex flex-col gap-0.5 max-h-[40vh] overflow-y-auto">
        {sorted.map((p) => {
          const isSelected = selectedId === p.id;
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelect(p.id)}
                className={cn(
                  "flex items-start gap-2 w-full rounded-lg px-2 py-1.5 text-left text-[11px] transition-colors cursor-pointer",
                  isSelected
                    ? "bg-primary/15 text-primary border border-primary/35 font-bold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent"
                )}
              >
                <FolderKanban
                  className={cn(
                    "w-3 h-3 shrink-0 mt-0.5",
                    isSelected && "text-primary"
                  )}
                />
                <span className="line-clamp-2 leading-snug">{p.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
